﻿import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Rocket, Sparkles, X, LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import * as authService from '../../services/authService';
import { encryptString, decryptString } from '../../utils/crypto';

const REMEMBER_KEY = 'xf_remember_me';

function loadSavedCredentials(): { username: string; password: string; remember: boolean } {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      return {
        username: data.username || '',
        password: data.password ? decryptString(data.password) : '',
        remember: !!data.remember,
      };
    }
  } catch (e: any) {
    console.warn('[Auth] 读取记住的凭据失败:', e?.message || e);
  }
  return { username: '', password: '', remember: false };
}

function saveCredentials(username: string, password: string, remember: boolean) {
  try {
    if (remember) {
      localStorage.setItem(REMEMBER_KEY, JSON.stringify({ username, password: encryptString(password), remember }));
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
  } catch (e: any) {
    console.warn('[Auth] 保存凭据失败:', e?.message || e);
  }
}

export function LoginRequired({ t, onLogin }: { t: any; onLogin: () => void }) {
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-8"
    >
      <div className="w-24 h-24 rounded-3xl bg-celestial-saturn/10 flex items-center justify-center text-celestial-saturn">
        <Sparkles size={48} />
      </div>
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tighter">{t.loginRequired || '需要登录'}</h2>
        <p className="text-white/40 max-w-md mx-auto">
          {t.loginRequiredDesc || '请连接您的账户以访问此模块'}
        </p>
      </div>
      <button
        onClick={onLogin}
        className="px-8 py-4 bg-celestial-saturn text-black font-bold rounded-full hover:scale-105 transition-transform flex items-center gap-2"
      >
        <Rocket size={20} />
        {t.connect || '连接'}
      </button>
    </motion.div>
  );
}

export function LoginModal({ t, isOpen, onClose, onLoginSuccess }: { t: any; isOpen: boolean; onClose: () => void; onLoginSuccess: () => void }) {
  const saved = loadSavedCredentials();
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState(saved.username);
  const [password, setPassword] = useState(saved.password);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(saved.remember);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ username?: string; password?: string }>({});

  if (!isOpen) return null;

  const validateForm = () => {
    const errs: { username?: string; password?: string } = {};
    if (!username.trim()) {
      errs.username = t.authUsernameRequired || '请输入用户名';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      errs.username = t.authValidationUsername || '用户名只能包含字母、数字、下划线和横线';
    }
    if (!password) {
      errs.password = t.authPasswordRequired || '请输入密码';
    } else if (password.length < 6) {
      errs.password = t.authPasswordTooShort || '密码长度至少 6 位';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // 表单字段校验（字段级错误提示）
    if (!validateForm()) {
      setLoading(false);
      return;
    }
    
    try {
      let result;
      if (isRegister) {
        const phoneRegex = /^\+?[0-9]{5,15}$/;
        if (!phoneRegex.test(phone)) {
          setError(t.invalidPhone || '手机号格式不正确');
          setLoading(false);
          return;
        }
        result = await authService.register(username, password, phone);
      } else {
        result = await authService.login(username, password);
      }

      if (result.success) {
        saveCredentials(username, password, rememberMe);
        onLoginSuccess();
        onClose();
      } else {
        setError(result.error || (isRegister ? (t.registerFailed || '注册失败') : (t.loginFailed || '登录失败')));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-[#111] border border-white/10 rounded-[2rem] w-full max-w-md overflow-hidden shadow-2xl relative"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-celestial-mars via-celestial-saturn to-celestial-mars" />
        
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl font-black tracking-tighter text-white">
                {isRegister ? t.register || '注册节点' : t.loginTitle || '同步节点'}
              </h2>
              <p className="text-white/40 text-xs font-bold uppercase tracking-widest mt-1">
                {t.authSubtitle || '小枫网络认证'}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-full text-white/40 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-white/30 ml-1">{t.username || '用户名'}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); if (fieldErrors.username) setFieldErrors({ ...fieldErrors, username: undefined }); }}
                required
                className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-white placeholder:text-white/35 focus:outline-none focus:border-celestial-saturn/50 transition-colors ${fieldErrors.username ? 'border-red-500/60' : 'border-white/10'}`}
                placeholder={t.authPlaceholderUsername || '输入用户名'}
              />
              {fieldErrors.username && (
                <p className="text-red-500 text-xs font-bold ml-1">{fieldErrors.username}</p>
              )}
            </div>

            {isRegister && (
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-widest text-white/30 ml-1">{t.phone || '手机号'}</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/35 focus:outline-none focus:border-celestial-saturn/50 transition-colors"
                  placeholder="+86..."
                />
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-white/30 ml-1">{t.password || '密码'}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (fieldErrors.password) setFieldErrors({ ...fieldErrors, password: undefined }); }}
                  required
                  className={`w-full bg-white/5 border rounded-xl px-4 py-3 pr-12 text-white placeholder:text-white/35 focus:outline-none focus:border-celestial-saturn/50 transition-colors ${fieldErrors.password ? 'border-red-500/60' : 'border-white/10'}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                  aria-label={showPassword ? (t.authHidePassword || '隐藏密码') : (t.authShowPassword || '显示密码')}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-red-500 text-xs font-bold ml-1">{fieldErrors.password}</p>
              )}
            </div>

            {!isRegister && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-celestial-saturn focus:ring-celestial-saturn/30"
                />
                <span className="text-xs text-white/40 font-medium">{t.rememberMe || '记住我'}</span>
              </label>
            )}

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-xs font-bold flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                {error}
              </div>
            )}

            <div className="flex flex-col gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 rounded-xl font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg ${
                  loading ? 'bg-white/10 text-white/20 cursor-not-allowed' : 'bg-white text-black hover:scale-[1.02] active:scale-[0.98]'
                }`}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    {isRegister ? <UserPlus size={18} /> : <LogIn size={18} />}
                    {isRegister ? t.registerBtn || '注册节点' : t.loginBtn || '建立连接'}
                  </>
                )}
              </button>

              

              <button
                type="button"
                onClick={() => setIsRegister(!isRegister)}
                className="text-xs font-black text-celestial-saturn uppercase tracking-widest hover:underline transition-all mt-2"
              >
                {isRegister ? t.hasAccount || '返回登录' : t.noAccount || '注册新账号'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
